import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, SlidersHorizontal, Grid, List, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Header from './components/Header25';
import Footer from './components/Footer25';

const Template25Shop = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('All');
    const [sortBy, setSortBy] = useState('featured');
    const [currentPage, setCurrentPage] = useState(1);
    const [wishlist, setWishlist] = useState({});
    const [hoveredCard, setHoveredCard] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState('all');

    const categories = ['All', 'Kurtas', 'Sarees', 'Sherwanis', 'Lehengas', 'Indo-Western', 'Accessories'];

    const priceRanges = [
        { value: 'all', label: 'All Prices' },
        { value: 'under-5000', label: 'Under ₹5,000' },
        { value: '5000-10000', label: '₹5,000 - ₹10,000' },
        { value: 'above-10000', label: 'Above ₹10,000' },
    ];

    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest First' },
    ];

    const products = [
        {
            id: 1,
            name: 'Classic Cotton Kurta',
            category: 'Kurtas',
            price: 3499,
            image: 'https://images.pexels.com/photos/8310281/pexels-photo-8310281.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: 'Bestseller',
        },
        {
            id: 2,
            name: 'Modern Sherwani Set',
            category: 'Sherwanis',
            price: 18999,
            image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: 'Premium',
        },
        {
            id: 3,
            name: 'Silk Blend Saree',
            category: 'Sarees',
            price: 9500,
            image: 'https://images.pexels.com/photos/12045033/pexels-photo-12045033.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: 'New',
        },
        {
            id: 4,
            name: 'Indo-Western Jacket',
            category: 'Indo-Western',
            price: 7800,
            image: 'https://images.pexels.com/photos/10032879/pexels-photo-10032879.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: null,
        },
        {
            id: 5,
            name: 'Linen Kurta Pajama',
            category: 'Kurtas',
            price: 4200,
            image: 'https://images.pexels.com/photos/6311652/pexels-photo-6311652.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: 'Popular',
        },
        {
            id: 6,
            name: 'Contemporary Lehenga',
            category: 'Lehengas',
            price: 14999,
            image: 'https://images.pexels.com/photos/8839887/pexels-photo-8839887.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: 'Trending',
        },
        {
            id: 7,
            name: 'Structured Nehru Jacket',
            category: 'Indo-Western',
            price: 6500,
            image: 'https://images.pexels.com/photos/9558577/pexels-photo-9558577.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: null,
        },
        {
            id: 8,
            name: 'Chanderi Silk Dupatta',
            category: 'Accessories',
            price: 3200,
            image: 'https://images.pexels.com/photos/10679171/pexels-photo-10679171.jpeg?auto=compress&cs=tinysrgb&w=800',
            tag: 'Handcrafted',
        },
    ];

    const filteredProducts = products.filter(p => {
        const categoryMatch = activeFilter === 'All' || p.category === activeFilter;
        let priceMatch = true;
        if (priceRange === 'under-5000') priceMatch = p.price < 5000;
        else if (priceRange === '5000-10000') priceMatch = p.price >= 5000 && p.price <= 10000;
        else if (priceRange === 'above-10000') priceMatch = p.price > 10000;
        return categoryMatch && priceMatch;
    });

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'price-high') return b.price - a.price;
        return 0;
    });

    const toggleWishlist = (id) => {
        setWishlist(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
                .sahaj-shop * { box-sizing: border-box; }
                .sahaj-shop { font-family: 'IBM Plex Sans', sans-serif; background: #FFFFFF; color: #111111; }
                .sahaj-heading { font-family: 'Space Grotesk', sans-serif; }
                .sahaj-card { border-radius: 0 !important; transition: transform 0.3s ease, box-shadow 0.3s ease; }
                .sahaj-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,0.1); }
                .sahaj-btn { border-radius: 0 !important; }
                .sahaj-img { border-radius: 0 !important; }
                .sahaj-filter-active { background: #0D7C66; color: #FFFFFF; }
                .sahaj-filter-inactive { background: #F8F9FA; color: #111111; border: 1px solid #DEE2E6; }
                .sahaj-filter-inactive:hover { border-color: #0D7C66; color: #0D7C66; }
            `}</style>
            <div className="sahaj-shop" style={{ minHeight: '100vh' }}>
                <Header />

                {/* Hero Banner */}
                <div style={{ background: '#111111', padding: '60px 0', marginTop: '80px' }}>
                    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
                        <h1 className="sahaj-heading" style={{
                            fontSize: 48, fontWeight: 700, color: '#FFFFFF', margin: 0, letterSpacing: '-1px'
                        }}>
                            THE COLLECTION
                        </h1>
                        <p style={{
                            fontSize: 16, color: '#9CA3AF', marginTop: 12, fontWeight: 300, letterSpacing: '0.5px'
                        }}>
                            Contemporary Indian wear, crafted with precision
                        </p>
                    </div>
                </div>

                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 24px 80px' }}>
                    {/* Toolbar */}
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: 32, flexWrap: 'wrap', gap: 16
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <button
                                className="sahaj-btn"
                                onClick={() => setShowFilters(!showFilters)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '10px 20px', border: '1px solid #DEE2E6',
                                    background: showFilters ? '#0D7C66' : '#FFFFFF',
                                    color: showFilters ? '#FFFFFF' : '#111111',
                                    cursor: 'pointer', fontSize: 14, fontWeight: 500,
                                    fontFamily: 'IBM Plex Sans, sans-serif'
                                }}
                            >
                                <SlidersHorizontal size={16} />
                                Filters
                            </button>
                            <span style={{ fontSize: 14, color: '#6C757D' }}>
                                {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="sahaj-btn"
                                style={{
                                    padding: '10px 16px', border: '1px solid #DEE2E6',
                                    background: '#FFFFFF', fontSize: 14, cursor: 'pointer',
                                    fontFamily: 'IBM Plex Sans, sans-serif', color: '#111111',
                                    outline: 'none'
                                }}
                            >
                                {sortOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <div style={{ display: 'flex', border: '1px solid #DEE2E6' }}>
                                <button
                                    onClick={() => setViewMode('grid')}
                                    style={{
                                        padding: '10px 12px', border: 'none', cursor: 'pointer',
                                        background: viewMode === 'grid' ? '#111111' : '#FFFFFF',
                                        color: viewMode === 'grid' ? '#FFFFFF' : '#6C757D',
                                    }}
                                >
                                    <Grid size={16} />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    style={{
                                        padding: '10px 12px', border: 'none', borderLeft: '1px solid #DEE2E6',
                                        cursor: 'pointer',
                                        background: viewMode === 'list' ? '#111111' : '#FFFFFF',
                                        color: viewMode === 'list' ? '#FFFFFF' : '#6C757D',
                                    }}
                                >
                                    <List size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div style={{
                            background: '#F8F9FA', padding: 24, marginBottom: 32,
                            border: '1px solid #DEE2E6'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                <h3 className="sahaj-heading" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                                    Filter By
                                </h3>
                                <button onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                    <X size={20} color="#6C757D" />
                                </button>
                            </div>
                            <div style={{ marginBottom: 20 }}>
                                <p style={{ fontSize: 13, fontWeight: 600, color: '#6C757D', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '1px' }}>Category</p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {categories.map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => { setActiveFilter(cat); setCurrentPage(1); }}
                                            className={`sahaj-btn ${activeFilter === cat ? 'sahaj-filter-active' : 'sahaj-filter-inactive'}`}
                                            style={{
                                                padding: '8px 20px', cursor: 'pointer',
                                                fontSize: 13, fontWeight: 500,
                                                fontFamily: 'IBM Plex Sans, sans-serif',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <p style={{ fontSize: 13, fontWeight: 600, color: '#6C757D', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '1px' }}>Price Range</p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {priceRanges.map(pr => (
                                        <button
                                            key={pr.value}
                                            onClick={() => { setPriceRange(pr.value); setCurrentPage(1); }}
                                            className={`sahaj-btn ${priceRange === pr.value ? 'sahaj-filter-active' : 'sahaj-filter-inactive'}`}
                                            style={{
                                                padding: '8px 20px', cursor: 'pointer',
                                                fontSize: 13, fontWeight: 500,
                                                fontFamily: 'IBM Plex Sans, sans-serif',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            {pr.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Active Filters Indicators */}
                    {(activeFilter !== 'All' || priceRange !== 'all') && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                            <span style={{ fontSize: 13, color: '#6C757D' }}>Active:</span>
                            {activeFilter !== 'All' && (
                                <span style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '4px 12px', background: '#E8F5F1', color: '#0D7C66',
                                    fontSize: 13, fontWeight: 500
                                }}>
                                    {activeFilter}
                                    <X size={14} style={{ cursor: 'pointer' }} onClick={() => setActiveFilter('All')} />
                                </span>
                            )}
                            {priceRange !== 'all' && (
                                <span style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '4px 12px', background: '#E8F5F1', color: '#0D7C66',
                                    fontSize: 13, fontWeight: 500
                                }}>
                                    {priceRanges.find(p => p.value === priceRange)?.label}
                                    <X size={14} style={{ cursor: 'pointer' }} onClick={() => setPriceRange('all')} />
                                </span>
                            )}
                        </div>
                    )}

                    {/* Product Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr',
                        gap: viewMode === 'grid' ? 24 : 16
                    }}>
                        {sortedProducts.map((product) => (
                            <div
                                key={product.id}
                                className="sahaj-card"
                                onMouseEnter={() => setHoveredCard(product.id)}
                                onMouseLeave={() => setHoveredCard(null)}
                                style={{
                                    background: '#FFFFFF',
                                    border: '1px solid #DEE2E6',
                                    cursor: 'pointer',
                                    display: viewMode === 'list' ? 'flex' : 'block',
                                    overflow: 'hidden'
                                }}
                                onClick={() => navigate('/template25/product')}
                            >
                                {/* Image */}
                                <div style={{
                                    position: 'relative', overflow: 'hidden',
                                    width: viewMode === 'list' ? 200 : '100%',
                                    minWidth: viewMode === 'list' ? 200 : 'auto',
                                    aspectRatio: '1/1'
                                }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="sahaj-img"
                                        style={{
                                            width: '100%', height: '100%', objectFit: 'cover',
                                            transition: 'transform 0.5s ease',
                                            transform: hoveredCard === product.id ? 'scale(1.05)' : 'scale(1)'
                                        }}
                                    />
                                    {product.tag && (
                                        <span style={{
                                            position: 'absolute', top: 12, left: 12,
                                            background: '#0D7C66', color: '#FFFFFF',
                                            padding: '4px 12px', fontSize: 11, fontWeight: 600,
                                            textTransform: 'uppercase', letterSpacing: '1px'
                                        }}>
                                            {product.tag}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
                                        style={{
                                            position: 'absolute', top: 12, right: 12,
                                            width: 40, height: 40, display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            background: '#FFFFFF', border: 'none', cursor: 'pointer',
                                            opacity: hoveredCard === product.id || wishlist[product.id] ? 1 : 0,
                                            transition: 'opacity 0.3s ease'
                                        }}
                                    >
                                        <Heart
                                            size={18}
                                            fill={wishlist[product.id] ? '#0D7C66' : 'none'}
                                            color={wishlist[product.id] ? '#0D7C66' : '#111111'}
                                        />
                                    </button>
                                    {hoveredCard === product.id && (
                                        <div style={{
                                            position: 'absolute', bottom: 0, left: 0, right: 0,
                                            padding: 12, background: 'rgba(0,0,0,0.8)',
                                            transition: 'opacity 0.3s ease'
                                        }}>
                                            <button
                                                className="sahaj-btn"
                                                style={{
                                                    width: '100%', padding: '10px', background: '#0D7C66',
                                                    color: '#FFFFFF', border: 'none', fontSize: 13,
                                                    fontWeight: 600, cursor: 'pointer', letterSpacing: '1px',
                                                    textTransform: 'uppercase',
                                                    fontFamily: 'Space Grotesk, sans-serif'
                                                }}
                                            >
                                                Quick View
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {/* Info */}
                                <div style={{ padding: viewMode === 'list' ? '20px 24px' : '16px 16px 20px', flex: 1 }}>
                                    <p style={{
                                        fontSize: 11, fontWeight: 600, color: '#0D7C66',
                                        textTransform: 'uppercase', letterSpacing: '1.5px',
                                        marginBottom: 6
                                    }}>
                                        {product.category}
                                    </p>
                                    <h3 className="sahaj-heading" style={{
                                        fontSize: viewMode === 'list' ? 20 : 16, fontWeight: 600,
                                        margin: '0 0 8px', lineHeight: 1.3
                                    }}>
                                        {product.name}
                                    </h3>
                                    <p className="sahaj-heading" style={{
                                        fontSize: 18, fontWeight: 700, color: '#111111', margin: 0
                                    }}>
                                        ₹{product.price.toLocaleString('en-IN')}
                                    </p>
                                    {viewMode === 'list' && (
                                        <button
                                            className="sahaj-btn"
                                            style={{
                                                marginTop: 16, padding: '10px 32px',
                                                background: '#0D7C66', color: '#FFFFFF',
                                                border: 'none', fontSize: 13, fontWeight: 600,
                                                cursor: 'pointer', textTransform: 'uppercase',
                                                letterSpacing: '1px',
                                                fontFamily: 'Space Grotesk, sans-serif'
                                            }}
                                        >
                                            View Details
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Empty State */}
                    {sortedProducts.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '80px 0' }}>
                            <p className="sahaj-heading" style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
                                No products found
                            </p>
                            <p style={{ color: '#6C757D', marginBottom: 24 }}>Try adjusting your filters</p>
                            <button
                                className="sahaj-btn"
                                onClick={() => { setActiveFilter('All'); setPriceRange('all'); }}
                                style={{
                                    padding: '12px 32px', background: '#0D7C66', color: '#FFFFFF',
                                    border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                    fontFamily: 'Space Grotesk, sans-serif'
                                }}
                            >
                                Clear All Filters
                            </button>
                        </div>
                    )}

                    {/* Pagination */}
                    {sortedProducts.length > 0 && (
                        <div style={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            gap: 8, marginTop: 60
                        }}>
                            <button
                                className="sahaj-btn"
                                style={{
                                    width: 44, height: 44, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', border: '1px solid #DEE2E6',
                                    background: '#FFFFFF', cursor: 'pointer'
                                }}
                            >
                                <ChevronLeft size={18} />
                            </button>
                            {[1, 2, 3].map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className="sahaj-btn"
                                    style={{
                                        width: 44, height: 44,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: currentPage === page ? 'none' : '1px solid #DEE2E6',
                                        background: currentPage === page ? '#111111' : '#FFFFFF',
                                        color: currentPage === page ? '#FFFFFF' : '#111111',
                                        cursor: 'pointer', fontSize: 14, fontWeight: 600,
                                        fontFamily: 'Space Grotesk, sans-serif'
                                    }}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="sahaj-btn"
                                style={{
                                    width: 44, height: 44, display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', border: '1px solid #DEE2E6',
                                    background: '#FFFFFF', cursor: 'pointer'
                                }}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    )}
                </div>

                <Footer />
            </div>
        </>
    );
};

export default Template25Shop;
